import { setValue, isStateForm, proxyArray } from './proxy/state/setValue';
import { getSectionParent, defineSectionAction } from './proxy/state/section';
import { proxyScope, proxyParent } from './proxy/state/index';
import proxyRouter from './proxy/router';
import { getAndProxyForm } from './proxy/proxyForm';
import proxyContext from './proxy/proxyContext';
import proxyIndex from './proxy/proxyIndex';

// object.defineProperty版本
// pros: 兼容性, 易测试
// cons: 性能, 内存，动态扩展
// proxy方案与本方案相反

/**
 * action/createContext line 72;
 * @param {obj} context 运行上下文
 * @param {obj} scope   运行的scope，不传的化，使用context.store
 */
/* eslint-disable no-use-before-define */
const RESERVED_KEY = {
  dp: true,
  ctx: true,
  remove: true,
  index: true,
  scope: true,
  iteratorId: true,
  util: true,
  router: true,
  form: true,
};
export default function proxyCreateContext(context, scope) {
  const _scope = scope || context.scope || context.getStore();
  let $scope;
  if (_scope && _scope.owner) {
    // use getValue to not cause report as observed
    $scope = _scope.owner.getValue();
  }
  let proxyProtoType = {};
  let proxy;
  if (scope && scope.type === 'LIST') {
    proxyProtoType = Object.create(Array.prototype);
    // 兼容 Array.isArry
    // @from vue@2.6.8
    proxy = [];
    proxyArray(proxy, proxyProtoType);
  } else {
    proxy = Object.create(proxyProtoType);
  }
  proxyProtoType.util = {};
  proxyContext(proxyProtoType, context);
  if (context.dp) {
    proxyProtoType.dp = {
      exec: context.dp.exec.bind(context.dp),
      getValue: context.dp.getValue.bind(context.dp),
      setValue: context.dp.setValue.bind(context.dp),
    };
  }
  Object.keys(context.fn).forEach((key) => {
    if (typeof context.fn[key] === 'function') {
      // 叫用函数时，默认使用自己作为this
      // 可以进行同步
      proxyProtoType.util[key] = (...args) => context.fn[key].apply(proxy, args);
    } else {
      proxyProtoType.util[key] = context.fn[key];
    }
  });
  proxyIndex(proxyProtoType, _scope);
  if (scope) {
    proxyProtoType.scope = $scope;
  }
  if (context.getComponentView) {
    proxyProtoType.ctx.getComponentView = context.getComponentView.bind(context);
  }
  const contextAction = context.getAction();
  const section = getSectionParent(_scope, context);
  if (section) {
    defineSectionAction({
      proxy: proxyProtoType, section, scope: _scope,
    });
  } else if (contextAction) {
    // 正常的scope
    const actions = contextAction.actionsMap;
    proxyProtoType.action = {};
    Object.keys(actions).forEach((actionName) => {
      proxyProtoType.action[actionName] = actions[actionName].func.bind(proxy, actionName);
    });
  }
  if (_scope && _scope.getItems && _scope.getItems()) {
    Object.defineProperty(proxy, 'items', {
      get() {
        return proxyCreateContext(context, _scope.getItems());
      },
    });
  }
  context.setParams = (params) => {
    context.params = {
      ...context.params,
      ...params,
    };
    proxy.ctx.params = context.params;
  };
  if (context.form) {
    proxy.ctx.form = context.form;
  }
  if (_scope && !_scope.isRoot) {
    Object.defineProperties(proxy, {
      store: {
        get() {
          return proxyCreateContext(context, context.getStore());
        },
      },
    });
  }
  proxyProtoType.router = {};
  proxyRouter(context, proxyProtoType.router);
  // proxy state
  let fieldId;
  let innerScope;
  // use getValue to not cause report as observed
  if (context.state && context.state.getValue('fieldId')) {
    fieldId = context.state.getValue('fieldId');
    innerScope = _scope;
  }
  const ownerScope = _scope && _scope.getOwnerScope && _scope.getOwnerScope();
  if (!fieldId && ownerScope) {
    // use getValue to not cause report as observed
    fieldId = _scope.getValue('fieldId');
    innerScope = ownerScope;
  }
  if (fieldId && innerScope && innerScope.get(fieldId)) {
    Object.defineProperties(proxy, {
      state: {
        get() {
          // 通过自己的fieldId来代理
          return proxyScope({
            ctx: context, scope: innerScope, prop: fieldId,
          });
        },
      },
    });
  }
  if (_scope) {
    if (!_scope.isRoot) {
      Object.defineProperty(proxy, 'parent', {
        get() {
          return proxyParent(context, _scope);
        },
      });
    }
    if (_scope.remove && _scope.owner) {
      proxyProtoType.remove = _scope.remove && _scope.remove.bind(_scope);
    }
    // state
    // prototype 定义完成，下面开始数据定义
    // const result = Object.create(proxy);
    const syncState = () => {
      // list state
      const { items } = _scope.data;
      // object state
      const states = _scope.data.maps;
      if (states || items) {
        Object.keys(states || items).forEach((prop) => {
          let point = proxy;
          if ((prop in RESERVED_KEY && prop in proxy) || prop === 'props') {
            if (!proxy.props) {
              proxy.props = {};
            }
            point = proxy.props;
          }
          if (prop in point) {
            return;
          }
          Object.defineProperty(point, prop, {
            get() {
              return proxyScope({
                ctx: context, scope: _scope, prop,
              });
            },
            set(value) {
              if (_scope.data.maps[prop].isState) {
                if (isStateForm(_scope.get(prop))) {
                  setValue(_scope.getForm(prop), value, _scope.get(prop));
                } else {
                  setValue(_scope.get(prop), value, _scope.get(prop), _scope.getForm(prop));
                }
              } else {
                _scope.set(prop, value);
              }
            },
            enumerable: true,
          });
        });
      }
      // 绑定父级 form 到 this
      if (!proxyProtoType.hasOwnProperty('form')) {
        Object.defineProperty(proxyProtoType, 'form', {
          get() {
            return getAndProxyForm(_scope, context);
          },
        });
      }
      // form
      const { subforms } = _scope;
      Object.keys(subforms || {}).filter(key => key).forEach((prop) => {
        if (subforms[prop] && subforms[prop].items && subforms[prop].items.length
          && (!(prop in proxy))) {
          Object.defineProperty(proxy, prop, {
            get() {
              return proxyScope({
                ctx: context, scope: _scope, prop,
              });
            },
            set(value) {
              return setValue(_scope.getForm(prop), value, _scope.get(prop));
            },
            enumerable: true,
          });
        }
      });
    };
    syncState();
    proxyProtoType.__syncState = syncState;
  }
  return proxy;
}

/* eslint-enable no-use-before-define */
